import type { Tour } from '../../../mocks/tours';

interface Activity {
  icon: string;
  text: string;
}

interface DayProgram {
  day: number;
  title: string;
  activities: Activity[];
}

const generateProgram = (tour: Tour): DayProgram[] => {
  const { duration, highlights } = tour;

  if (duration === 1) {
    return [
      {
        day: 1,
        title: 'Tur Günü — Gidiş & Dönüş',
        activities: [
          { icon: 'ri-map-pin-2-line', text: '07:00 — Ankara\'dan hareket' },
          { icon: 'ri-map-pin-line', text: `10:00 — ${highlights[0]} ziyareti ve fotoğraf molası` },
          { icon: 'ri-restaurant-line', text: '12:30 — Öğle yemeği molası (kendi hesabına)' },
          ...(highlights[1] ? [{ icon: 'ri-map-pin-line', text: `14:00 — ${highlights[1]} keşfi` }] : []),
          ...(highlights[2] ? [{ icon: 'ri-shopping-bag-line', text: `16:00 — ${highlights[2]} ve serbest alışveriş` }] : []),
          { icon: 'ri-arrow-left-line', text: '17:30 — Ankara\'ya dönüş yolculuğu' },
          { icon: 'ri-home-4-line', text: '21:00 — Ankara\'ya varış' },
        ],
      },
    ];
  }

  const programs: DayProgram[] = [];
  for (let i = 0; i < duration; i++) {
    if (i === 0) {
      programs.push({
        day: 1,
        title: '1. Gün — Hareket & Karşılama',
        activities: [
          { icon: 'ri-map-pin-2-line', text: 'Sabah erken — Ankara\'dan hareket' },
          { icon: 'ri-map-pin-line', text: `Öğleden sonra — ${highlights[0]} ziyareti` },
          { icon: 'ri-hotel-line', text: 'Akşam — Otele yerleşme' },
          { icon: 'ri-moon-line', text: 'Gece konaklaması' },
        ],
      });
    } else if (i === duration - 1) {
      programs.push({
        day: duration,
        title: `${duration}. Gün — Son Gün & Dönüş`,
        activities: [
          { icon: 'ri-coffee-line', text: 'Sabah — Kahvaltı ve otelden çıkış' },
          { icon: 'ri-shopping-bag-line', text: 'Serbest zaman ve son alışveriş' },
          { icon: 'ri-arrow-left-line', text: 'Öğleden sonra — Ankara\'ya dönüş' },
          { icon: 'ri-home-4-line', text: 'Akşam — Ankara\'ya varış' },
        ],
      });
    } else {
      programs.push({
        day: i + 1,
        title: `${i + 1}. Gün — Keşif & Gezi`,
        activities: [
          { icon: 'ri-coffee-line', text: 'Sabah — Kahvaltı' },
          { icon: 'ri-map-pin-line', text: `Sabah — ${highlights[i % highlights.length]} keşfi ve rehberli gezi` },
          { icon: 'ri-restaurant-line', text: 'Öğle — Yöresel restoranda öğle yemeği' },
          {
            icon: 'ri-map-pin-line',
            text: `Öğleden sonra — ${highlights[(i + 1) % highlights.length]} ziyareti ve serbest zaman`,
          },
          { icon: 'ri-hotel-line', text: 'Akşam yemeği ve gece konaklaması' },
        ],
      });
    }
  }

  return programs;
};

export default function ProgramSection({ tour }: { tour: Tour }) {
  const programs = generateProgram(tour);

  return (
    <div className="bg-white rounded-2xl p-7 border border-gray-100">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-9 h-9 flex items-center justify-center bg-orange-50 rounded-xl">
          <i className="ri-calendar-schedule-line text-orange-500 text-lg" />
        </div>
        <h2 className="text-lg font-bold text-gray-900">Tur Programı</h2>
      </div>

      <div className="space-y-6">
        {programs.map((dayProg, idx) => (
          <div key={dayProg.day} className="flex gap-4">
            {/* Day indicator */}
            <div className="flex flex-col items-center flex-shrink-0">
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-orange-500 text-white text-sm font-bold">
                {dayProg.day}
              </div>
              {idx < programs.length - 1 && (
                <div className="w-px flex-1 bg-orange-200 mt-2" />
              )}
            </div>
            {/* Content */}
            <div className="pb-6 flex-1">
              <h3 className="font-semibold text-gray-900 mb-3 text-sm">{dayProg.title}</h3>
              <ul className="space-y-2">
                {dayProg.activities.map((act, aIdx) => (
                  <li key={aIdx} className="flex items-start gap-2.5 text-sm text-gray-600">
                    <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <i className={`${act.icon} text-orange-400 text-xs`} />
                    </div>
                    <span>{act.text}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
